import { z } from 'zod'
import { addressSchema } from './address'

// Risk assessment questionnaire schemas
const lookupKey = z.object({
  creationSystem: z.string(),
  creationKey: z.string(),
})

const riskAssessmentItemSchema = z.object({
  lookupKey,
  name: z.string(),
})

const occupation = riskAssessmentItemSchema.extend({
  industryGrouping: z.string(),
  industryName: z.string(),
})

const employmentStatus = riskAssessmentItemSchema.extend({
  industryGrouping: z.string(),
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const riskAssessmentSchema = z.object({
  employmentStatuses: z.array(employmentStatus),
  occupations: z.array(occupation),
  industryParticipation: z.array(riskAssessmentItemSchema),
  bettingCustomerTypes: z.array(riskAssessmentItemSchema),
})

export type RiskAssessmentItem = z.infer<typeof riskAssessmentItemSchema>
export type Occupation = z.infer<typeof occupation>
export type EmploymentStatus = z.infer<typeof employmentStatus>
export type LookupKey = z.infer<typeof lookupKey>
export type RiskAssessment = z.infer<typeof riskAssessmentSchema>

// Application save schemas
const applicantDetailsSchema = z.object({
  firstName: z.string(),
  middleName: z.string(),
  lastName: z.string(),
  firstNameAlias: z.string().nullable().optional(),
  lastNameAlias: z.string().nullable().optional(),
  birthdate: z.string().datetime(),
  email: z.string().email(),
  mobileNumber: z.string(),
  residentialAddress: addressSchema,
  previousAddress: addressSchema.nullable().optional(),
})

const applicantOptionsSchema = z.object({
  marketingOptInIndicator: z.boolean(),
  isIdVerificationOnlyAccount: z.boolean(),
})

const applicationDetailsSchema = z.object({
  userAgent: z.string(),
  openingLocation: z.string(),
  gpsLocation: z.string(),
  gpsVenue: z.string(),
  deviceLatitude: z.number().nullable().optional(),
  deviceLongitude: z.number().nullable().optional(),
  ipAddress: z.string(),
  affiliateMarketingId: z.string().nullable().optional(),
  locationId: z.string().nullable().optional(),
  venueId: z.string().nullable().optional(),
})

const applicantRiskDetailSchema = z.object({
  employmentStatus: lookupKey,
  employmentOccupation: lookupKey,
  industryParticipantType: z.array(lookupKey),
  bettingCustomerType: z.array(lookupKey),
})

const driverLicenceSchema = z.object({
  number: z.string(),
  state: z.string(),
  cardNumber: z.string(),
})

const passportSchema = z.object({
  number: z.string(),
  passportCountry: z.string(),
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const applicationRequestSchema = z.object({
  applicantDetails: applicantDetailsSchema,
  applicantOptions: applicantOptionsSchema,
  applicationDetails: applicationDetailsSchema.nullable().optional(),
  applicantRiskDetails: applicantRiskDetailSchema.nullable().optional(),
  driverLicenceDetails: driverLicenceSchema.nullable().optional(),
  passportDetails: passportSchema.nullable().optional(),
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const applicationResponseSchema = z.object({
  applicationReferenceNumber: z.string(),
  duplicateExists: z.boolean(),
  isDuplicateClosed: z.boolean(),
})

export type ApplicantDetails = z.infer<typeof applicantDetailsSchema>
export type ApplicantOptions = z.infer<typeof applicantOptionsSchema>
export type ApplicationDetails = z.infer<typeof applicationDetailsSchema>
export type ApplicantRiskDetail = z.infer<typeof applicantRiskDetailSchema>
export type DriverLicenceDetails = z.infer<typeof driverLicenceSchema>
export type PassportDetails = z.infer<typeof passportSchema>
export type ApplicationRequest = z.infer<typeof applicationRequestSchema>
export type ApplicationResponse = z.infer<typeof applicationResponseSchema>

// Application submit schemas
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const applicationSubmitRequestSchema = z.object({
  termsAndConditionsText: z.string(),
  termsAndConditionsConsent: z.boolean(),
})
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const applicationSubmitResponseSchema = z.object({
  enquiryId: z.string(),
  isVerified: z.boolean(),
  unverifiedReason: z.string().nullable().optional(),
  pendingReview: z.boolean(),
})

export type ApplicationSubmitRequest = z.infer<typeof applicationSubmitRequestSchema>
export type ApplicationSubmitResponse = z.infer<typeof applicationSubmitResponseSchema>
